/**
 * v-img-fallback 指令
 * @description 为 <img> 标签提供加载失败时的自动占位图回退
 *
 * @example
 * ```vue
 * <!-- 使用默认占位图 -->
 * <img v-img-fallback src="..." alt="..." />
 *
 * <!-- 使用自定义占位图 -->
 * <img v-img-fallback="'/images/custom-placeholder.png'" src="..." alt="..." />
 * ```
 */
import type { Directive, DirectiveBinding } from 'vue'
import placeholderImage from '@/assets/images/common/placeholder-image.svg'

/** 标记已处理的 img，避免重复绑定 */
const FALLBACK_BOUND_KEY = '__imgFallbackBound__'

/**
 * 获取 fallback 图片地址
 * 优先使用指令绑定值，否则使用默认占位图
 */
function getFallbackSrc(binding: DirectiveBinding<string | undefined>): string {
  return binding.value || placeholderImage
}

/**
 * 为 img 元素绑定 error 事件处理
 */
function bindFallback(el: HTMLImageElement, binding: DirectiveBinding<string | undefined>): void {
  // 避免重复绑定
  if ((el as HTMLImageElement & { [FALLBACK_BOUND_KEY]?: boolean })[FALLBACK_BOUND_KEY]) {
    return
  }

  const fallbackSrc = getFallbackSrc(binding)

  el.addEventListener('error', function onError() {
    // 仅在 src 不是 fallback 自身时替换，防止无限循环
    if (el.src !== fallbackSrc) {
      el.src = fallbackSrc
    }
    // 一次性处理：替换后移除监听器
    el.removeEventListener('error', onError)
  })

  ;(el as HTMLImageElement & { [FALLBACK_BOUND_KEY]?: boolean })[FALLBACK_BOUND_KEY] = true
}

/**
 * v-img-fallback 自定义指令
 */
export const vImgFallback: Directive<HTMLImageElement, string | undefined> = {
  mounted(el, binding) {
    bindFallback(el, binding)
  },
  updated(el, binding) {
    // src 变更时重新绑定
    if (binding.value !== binding.oldValue) {
      ;(el as HTMLImageElement & { [FALLBACK_BOUND_KEY]?: boolean })[FALLBACK_BOUND_KEY] = false
      bindFallback(el, binding)
    }
  },
}
